import React, { useEffect, useState } from "react";
import IOSSpinner from "../../../components/IOSSpinner";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";
import BentoTable from "../../../components/BentoTable";
import ParcelCard from "./ParcelCard";
import { differenceInDays } from "date-fns";
import { dualNotification } from "../../../utils/EleghoUtils";
import toast from "react-hot-toast";
import { adminEmailAddress, adminEmailName } from "../../../utils/constants";

export default function ParcelAdminPage() {
  const [parcels, setParcels] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [numberOfItemPerPage, setNumberOfItemPerPage] = React.useState(5);
  const [activeFilter, setActiveFilter] = React.useState("stored");
  const [view, setView] = React.useState("standard");
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filters = ["All", "Stored", "Claimed"];

  useEffect(() => {
    const fetchParcels = async () => {
      await getAllDocsSnap("parcels", (data) => {
        data.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setParcels(data);
        setLoading(false);
      });
    };
    fetchParcels();
  }, []);

  const handleSendReminder = async (parcel) => {
    toast.error(`User for unit ${parcel.unitNumber} not found`);
    // try {
    //   setSendingReminder(true);
    //   const daysUnclaimed = getDaysUnclaimed(parcel);

    //   const toastId = toast.loading(
    //     `Sending reminder to ${parcel.recipientName}...`
    //   );

    //   await dualNotification({
    //     recipient: parcel.recipientEmail || parcel.email,
    //     recipientName: parcel.recipientName,
    //     sender: adminEmailAddress,
    //     senderName: adminEmailName,
    //     subject: "Parcel Reminder",
    //     message: `This is a reminder that you have a parcel (Parcel #${parcel.parcelNumber}) that has been unclaimed for ${daysUnclaimed} days. Please pick it up at your earliest convenience.`,
    //     type: "parcel",
    //     id: parcel.id,
    //     url: "/user/parcels",
    //   });

    //   toast.success(`Reminder sent to ${parcel.recipientName}`, {
    //     id: toastId,
    //   });
    // } catch (error) {
    //   console.error("Error sending reminder:", error);
    //   toast.error("Failed to send reminder");
    // } finally {
    //   setSendingReminder(false);
    // }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <IOSSpinner />
          <p className="mt-4 text-gray-600">Loading parcels...</p>
        </div>
      </div>
    );
  }

  // Filter parcels by status
  const statusFilteredParcels = parcels.filter((parcel) => {
    if (activeFilter === "all") return true;
    return parcel.status.toLowerCase() === activeFilter.toLowerCase();
  });

  // Further filter parcels by search query
  const filteredParcels = statusFilteredParcels.filter((parcel) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();
    return (
      (parcel.recipientName &&
        parcel.recipientName.toLowerCase().includes(query)) ||
      (parcel.parcelNumber && parcel.parcelNumber.toString().includes(query)) ||
      (parcel.unitNumber && parcel.unitNumber.toLowerCase().includes(query)) ||
      (parcel.deliveryCompany &&
        parcel.deliveryCompany.toLowerCase().includes(query))
    );
  });

  // Calculate counts for each filter category
  const parcelCounts = {
    all: parcels.length,
    stored: parcels.filter((parcel) => parcel.status.toLowerCase() === "stored")
      .length,
    claimed: parcels.filter(
      (parcel) => parcel.status.toLowerCase() === "claimed"
    ).length,
  };

  // Calculate days unclaimed for a parcel
  const getDaysUnclaimed = (parcel) => {
    if (parcel.status.toLowerCase() === "claimed") return 0;
    const creationDate = new Date(parcel.createdAt);
    const today = new Date();
    return differenceInDays(today, creationDate);
  };

  // Get color for days unclaimed
  const getAgingColor = (days) => {
    if (days <= 3) return "text-green-600";
    if (days <= 7) return "text-yellow-600";
    return "text-red-600";
  };

  // Sort parcels by age for aging view
  const agingParcels = [...filteredParcels].sort((a, b) => {
    // First sort by claimed status (unclaimed first)
    if (
      a.status.toLowerCase() === "claimed" &&
      b.status.toLowerCase() !== "claimed"
    )
      return 1;
    if (
      a.status.toLowerCase() !== "claimed" &&
      b.status.toLowerCase() === "claimed"
    )
      return -1;

    // Then sort by age (oldest first)
    return getDaysUnclaimed(b) - getDaysUnclaimed(a);
  });

  const slicedData =
    view === "standard"
      ? filteredParcels.slice(
          (currentPage - 1) * numberOfItemPerPage,
          currentPage * numberOfItemPerPage
        )
      : agingParcels.slice(
          (currentPage - 1) * numberOfItemPerPage,
          currentPage * numberOfItemPerPage
        );

  return (
    <div>
      <div className="title-header">Parcels</div>

      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {filters.map((filter, index) => (
            <div
              key={index}
              className={`px-4 py-2 rounded-lg cursor-pointer flex items-center ${
                activeFilter === filter.toLowerCase()
                  ? "bg-eleghoBlue text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
              onClick={() => setActiveFilter(filter.toLowerCase())}
            >
              <span>{filter}</span>
              <span
                className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  activeFilter === filter.toLowerCase()
                    ? "bg-white text-eleghoBlue"
                    : "bg-gray-300 text-gray-800"
                }`}
              >
                {parcelCounts[filter.toLowerCase()]}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`px-4 py-2 rounded-lg cursor-pointer ${
              view === "standard"
                ? "bg-eleghoBlue text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setView("standard")}
          >
            Standard View
          </div>
          <div
            className={`px-4 py-2 rounded-lg cursor-pointer ${
              view === "aging"
                ? "bg-eleghoBlue text-white"
                : "bg-gray-200 text-gray-700"
            }`}
            onClick={() => setView("aging")}
          >
            Aging View
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              ></path>
            </svg>
          </div>
          <input
            type="text"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
            placeholder="Search by recipient, parcel #, unit #, or delivery company"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredParcels.length} results for "{searchQuery}"
          </p>
        )}
      </div>

      <div className="mt-4">
        {view === "standard" ? (
          <BentoTable
            label={`Parcel Management (${filteredParcels.length} ${activeFilter} parcels)`}
            data={parcels}
            numberOfItemPerPage={numberOfItemPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          >
            {slicedData.map((item, index) => (
              <ParcelCard data={item} key={index} />
            ))}
          </BentoTable>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Parcel Aging Analysis
            </h3>

            {/* Parcel Detail Modal */}
            {selectedParcel && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold">Parcel Details</h3>
                      <button
                        onClick={() => setSelectedParcel(null)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Parcel Image */}
                      <div className="w-full md:w-1/3">
                        <img
                          src={selectedParcel.imageUrl}
                          alt={`Parcel ${selectedParcel.parcelNumber}`}
                          className="w-full h-auto object-cover rounded-lg"
                        />
                      </div>

                      {/* Parcel Info */}
                      <div className="w-full md:w-2/3">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-semibold">
                              Parcel #{selectedParcel.parcelNumber}
                            </h4>
                            <p className="text-gray-600">
                              {selectedParcel.unitNumber}
                            </p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              selectedParcel.status.toLowerCase() === "claimed"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {selectedParcel.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Recipient</p>
                            <p className="font-medium">
                              {selectedParcel.recipientName}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">
                              Delivery Company
                            </p>
                            <p className="font-medium">
                              {selectedParcel.deliveryCompany}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">
                              Storage Location
                            </p>
                            <p className="font-medium">
                              {selectedParcel.storageLocation}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Created By</p>
                            <p className="font-medium">
                              {selectedParcel.createdBy}
                            </p>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 mb-4">
                          <p>
                            Created:{" "}
                            {new Date(
                              selectedParcel.createdAt
                            ).toLocaleString()}
                          </p>
                          {selectedParcel.collectedAt && (
                            <p>
                              Collected:{" "}
                              {new Date(
                                selectedParcel.collectedAt
                              ).toLocaleString()}
                            </p>
                          )}
                        </div>

                        {selectedParcel.status.toLowerCase() !== "claimed" && (
                          <div>
                            <p className="text-sm font-medium mb-2">
                              <span
                                className={getAgingColor(
                                  getDaysUnclaimed(selectedParcel)
                                )}
                              >
                                This parcel has been unclaimed for{" "}
                                {getDaysUnclaimed(selectedParcel)} days
                              </span>
                            </p>
                            <button
                              onClick={() => handleSendReminder(selectedParcel)}
                              disabled={sendingReminder}
                              className="px-4 py-2 bg-eleghoBlue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {sendingReminder ? "Sending..." : "Send Reminder"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parcel #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Age (Days)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {slicedData.length > 0 ? (
                    slicedData.map((item, index) => {
                      const daysUnclaimed = getDaysUnclaimed(item);
                      return (
                        <tr
                          key={index}
                          className={`${
                            item.status.toLowerCase() !== "claimed"
                              ? "bg-gray-50"
                              : ""
                          } hover:bg-gray-100 cursor-pointer`}
                          onClick={() => setSelectedParcel(item)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.parcelNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.unitNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.recipientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                item.status.toLowerCase() === "claimed"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.status.toLowerCase() !== "claimed" ? (
                              <span
                                className={`text-sm font-medium ${getAgingColor(
                                  daysUnclaimed
                                )}`}
                              >
                                {daysUnclaimed}{" "}
                                {daysUnclaimed === 1 ? "day" : "days"}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.status.toLowerCase() !== "claimed" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendReminder(item);
                                }}
                                disabled={sendingReminder}
                                className="px-3 py-1 bg-eleghoBlue text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Remind
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        {searchQuery
                          ? "No parcels match your search criteria"
                          : "No parcels found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                <div className="flex-1 flex justify-between">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={
                      currentPage * numberOfItemPerPage >=
                      (view === "standard"
                        ? filteredParcels.length
                        : agingParcels.length)
                    }
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage * numberOfItemPerPage >=
                      (view === "standard"
                        ? filteredParcels.length
                        : agingParcels.length)
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
